import type { Node as JsonNode } from 'jsonc-parser';
import type { SizeUnits } from '../models/viewer-types';
import { formatFileSize } from '../utils/file-size';

const MAX_DEPTH = 100;
const MAX_OUTPUT = 8 * 1024 * 1024;

/** The shape the serializer walks: a value, a mapping of names, or a sequence of values. */
interface YamlNode {
  scalar?: string;
  entries?: Array<[string, YamlNode]>;
  items?: Array<YamlNode>;
}

const scalar = (value: string): YamlNode => ({ scalar: value });
const mapping = (entries: Array<[string, YamlNode]>): YamlNode => ({ entries });
const sequence = (items: Array<YamlNode>): YamlNode => ({ items });

function checkDepth(depth: number): void {
  if (depth > MAX_DEPTH) throw Error('YAML preview is limited to 100 nesting levels.');
}

// A plain name stands as it is; anything else, including a word YAML reads as a boolean, is quoted.
function key(value: string): string {
  return /^[a-z_][a-z0-9_-]*$/i.test(value) && !/^(true|false|yes|no|on|off|null)$/i.test(value) ? value : JSON.stringify(value);
}

function serialize(root: YamlNode, sizeUnits?: SizeUnits): string {
  const output: Array<string> = [];
  let length = 0;

  function append(line: string): void {
    length += line.length + 1;

    if (length > MAX_OUTPUT) throw Error(`YAML preview exceeds the ${formatFileSize(MAX_OUTPUT, sizeUnits)} text limit.`);

    output.push(line);
  }

  // What fits on the line that introduces it: a value, or a collection with nothing in it.
  function inline(node: YamlNode): string | null {
    if (node.scalar != null) return node.scalar;

    if (node.entries?.length === 0) return '{}';

    if (node.items?.length === 0) return '[]';

    return null;
  }

  function emit(node: YamlNode, depth: number, prefix = ''): void {
    checkDepth(depth);

    const indent = '  '.repeat(depth);
    const value = inline(node);

    if (value != null) {
      append(indent + prefix + value);

      return;
    }

    if (prefix) append(indent + prefix.trimEnd());

    const level = depth + (prefix ? 1 : 0);

    for (const [name, child] of node.entries || []) emit(child, level, `${key(name)}: `);

    for (const child of node.items || []) emit(child, level, '- ');
  }

  emit(root, 0);

  return output.join('\n');
}

/**
 * YAML for a parsed JSON document. Numbers are taken from the source text rather than from the parsed
 * value, so that what is shown keeps the precision the file was written with.
 *
 * @param {JsonNode} tree The parsed document.
 * @param {string} source The text it was parsed from.
 * @param {SizeUnits} [sizeUnits] Which unit system the size limit is spoken in.
 * @return {string} The document as YAML.
 */
export function jsonTreeToYaml(tree: JsonNode, source: string, sizeUnits?: SizeUnits): string {
  function convert(node: JsonNode, depth: number): YamlNode {
    checkDepth(depth);

    if (node.type === 'object') {
      const properties = node.children || [];
      const names = properties.map((property) => (property.children as Array<JsonNode>)[0].value as string);

      // YAML has no way to tell two identical keys apart, and silently dropping one would be a lie.
      if (new Set(names).size !== names.length) {
        throw Error('YAML preview cannot represent duplicate JSON keys. Use Original instead.');
      }

      return mapping(
        properties.map((property) => {
          const [name, value] = property.children as Array<JsonNode>;

          return [name.value as string, convert(value, depth + 1)];
        })
      );
    }

    if (node.type === 'array') return sequence((node.children || []).map((child) => convert(child, depth + 1)));

    return scalar(node.type === 'string' ? JSON.stringify(node.value) : source.slice(node.offset, node.offset + node.length));
  }

  return serialize(convert(tree, 0), sizeUnits);
}

/**
 * YAML for an XML document. Attributes are named with a leading `@`, and an element that mixes text
 * with children keeps their order in a `#content` sequence rather than losing it.
 *
 * @param {Document} document The parsed document.
 * @param {SizeUnits} [sizeUnits] Which unit system the size limit is spoken in.
 * @return {string} The document as YAML.
 */
export function xmlToYaml(document: Document, sizeUnits?: SizeUnits): string {
  function convert(element: Element, depth: number): YamlNode {
    checkDepth(depth);

    const attributes: Array<[string, YamlNode]> = [...element.attributes].map((attribute) => [
      `@${attribute.name}`,
      scalar(JSON.stringify(attribute.value))
    ]);
    const children = [...element.children];
    const textNodes = [...element.childNodes].filter((node) => node.nodeType === 3 || node.nodeType === 4);
    const text = textNodes.map((node) => node.textContent).join('');

    if (!children.length) {
      if (!attributes.length) return scalar(JSON.stringify(text));

      if (text) attributes.push(['#text', scalar(JSON.stringify(text))]);

      return mapping(attributes);
    }

    if (text.trim()) {
      attributes.push([
        '#content',
        sequence(
          [...element.childNodes].flatMap((node) => {
            if (node.nodeType === 1) return [mapping([[node.nodeName, convert(node as Element, depth + 1)]])];

            if (node.nodeType === 3 || node.nodeType === 4) return [scalar(JSON.stringify(node.textContent))];

            return [];
          })
        )
      ]);

      return mapping(attributes);
    }

    // Elements of the same name read as one sequence, which is what repetition means in XML.
    const groups = new Map<string, Array<YamlNode>>();

    for (const child of children) {
      if (!groups.has(child.nodeName)) groups.set(child.nodeName, []);

      (groups.get(child.nodeName) as Array<YamlNode>).push(convert(child, depth + 1));
    }

    for (const [name, values] of groups) attributes.push([name, values.length === 1 ? values[0] : sequence(values)]);

    return mapping(attributes);
  }

  const root = document.documentElement;

  return serialize(mapping([[root.nodeName, convert(root, 0)]]), sizeUnits);
}
